import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { NoticeItem, NoticeType, Gelanggang, UserProfile, Role } from '../../types';
import { getNoticesDB, saveNoticeDB, updateNoticeDB, deleteNoticeDB, getMembersDB, recordPracticeRsvpDB } from '../../services/db';
import { NoticeCardSkeleton } from '../../components/common/Skeleton';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  Plus,
  Calendar,
  CheckCircle2,
  XCircle,
  HelpCircle,
  BarChart3,
  Trash2,
  Edit3,
  ArrowRight,
  MessageSquare,
  List,
  Grid,
  ChevronRight,
  MapPin,
  ArrowLeft,
  Award,
  QrCode,
  CreditCard,
  Settings,
  Check,
  Sparkles,
  Armchair,
  Utensils,
  Bell,
  Clock,
  Flame,
  UserCheck
} from 'lucide-react';
import { canUserAddChild, isSuperAdmin, sanitizeMemberId, isChildOfParent, hasRolePermission, getHighestRole, formatEventDates, formatEventDatesFull } from '../../utils/formatters';
export const NoticeManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [layoutMode, setLayoutMode] = useState<'grid' | 'list'>('grid');
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveLayout = isMobile ? 'grid' : layoutMode;

  // Routing State for Single Notice View
  const [selectedNoticeIdForView, setSelectedNoticeIdForView] = useState<string | null>(() => {
    const parts = window.location.pathname.split('/');
    if (parts.length > 2 && parts[1] === 'notices') {
      return parts[2];
    }
    return null;
  });

  useEffect(() => {
    const handlePopState = () => {
      const parts = window.location.pathname.split('/');
      if (parts.length > 2 && parts[1] === 'notices') {
        setSelectedNoticeIdForView(parts[2]);
      } else {
        setSelectedNoticeIdForView(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToNotice = (noticeId: string | null) => {
    if (noticeId) {
      saveViewScroll('notices_list');
      setSelectedNoticeIdForView(noticeId);
      const targetPath = `/notices/${noticeId}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
      }
      resetScrollToTop();
    } else {
      setSelectedNoticeIdForView(null);
      if (window.location.pathname !== '/notices') {
        window.history.pushState(null, '', '/notices');
      }
      restoreViewScroll('notices_list');
    }
  };

  // Waris children list
  const [members, setMembers] = useState<UserProfile[]>([]);
  useEffect(() => {
    if (canUserAddChild(currentUser)) {
      getMembersDB().then(setMembers).catch(() => { });
    }
  }, [currentUser]);

  const myChildren = members.filter(m => isChildOfParent(m, currentUser) && m.status === 'Aktif');

  useEffect(() => {
    // Read from persistent cache first for instant sub-second render
    const cached = localStorage.getItem('asmm_db_notices_v4');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setNotices(parsed.filter((n: NoticeItem) => n.id !== 'notice-01' && !n.title.includes('Mesyuarat Agung Tahunan ASMM 2026')));
        }
      } catch { }
      setIsLoading(false);
    }

    // Silent background revalidation with aaPanel DB
    getNoticesDB().then((data) => {
      setNotices(data);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });

    const handleDbUpdate = () => {
      getNoticesDB().then((data) => {
        setNotices(data);
      }).catch(() => { });
    };
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, []);

  // Modal State for Notice Creation / Editing
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<NoticeType>('acara');
  const [targetGelanggang, setTargetGelanggang] = useState<Gelanggang>('Semua Gelanggang');
  const [eventDate, setEventDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [eventTime, setEventTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [ctaButtonLink, setCtaButtonLink] = useState('/payments');
  const [pollOptionsInput, setPollOptionsInput] = useState('Option 1, Option 2, Option 3');

  // Modal State for Breakdown Results
  const [breakdownNotice, setBreakdownNotice] = useState<NoticeItem | null>(null);

  // Add-on & Family package form states (Editable CRUD for all Acara)
  const [hasFamilyAddon, setHasFamilyAddon] = useState<boolean>(false);
  const [freeSeatsPerPax, setFreeSeatsPerPax] = useState<number>(2);
  const [freeMealsPerPax, setFreeMealsPerPax] = useState<number>(2);
  const [seatOriginalPrice, setSeatOriginalPrice] = useState<number>(37);
  const [seatPromoPrice, setSeatPromoPrice] = useState<number>(17);
  const [mealOriginalPrice, setMealOriginalPrice] = useState<number>(18);
  const [mealPromoPrice, setMealPromoPrice] = useState<number>(13);
  const [promoDeadline, setPromoDeadline] = useState<string>('2026-11-30');

  // Persistent Ujian Bengkung & Notice Payment Method Configuration
  const [ujianPaymentConfig, setUjianPaymentConfig] = useState<{
    paymentMethod: 'fpx' | 'qr' | 'both';
    qrBankName: string;
    qrAccountName: string;
    qrAccountNo: string;
    qrNotes: string;
  }>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('asmm_ujian_payment_config');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch { }
      }
    }
    return {
      paymentMethod: 'qr',
      qrBankName: 'Bank Islam Malaysia Berhad',
      qrAccountName: 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA',
      qrAccountNo: '02011010087654',
      qrNotes: 'Sila masukkan rujukan "UB2026 - [Nama Calon]" semasa pindahan DuitNow.'
    };
  });

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [editQrBankName, setEditQrBankName] = useState(ujianPaymentConfig.qrBankName);
  const [editQrAccountName, setEditQrAccountName] = useState(ujianPaymentConfig.qrAccountName);
  const [editQrAccountNo, setEditQrAccountNo] = useState(ujianPaymentConfig.qrAccountNo);
  const [editQrNotes, setEditQrNotes] = useState(ujianPaymentConfig.qrNotes);

  const handleUpdateUjianPaymentMethod = (method: 'fpx' | 'qr' | 'both') => {
    const updated = { ...ujianPaymentConfig, paymentMethod: method };
    setUjianPaymentConfig(updated);
    localStorage.setItem('asmm_ujian_payment_config', JSON.stringify(updated));
    window.dispatchEvent(new Event('asmm_db_updated'));
    showToast(
      'success',
      'Kaedah Bayaran Dikemaskini',
      `Sistem kini menggunakan: ${method === 'fpx' ? 'FPX Sahaja (ToyyibPay)' : method === 'qr' ? 'DuitNow QR Sahaja' : 'Kedua-duanya (FPX & QR)'}`
    );
  };

  const handleSaveQrDetails = () => {
    const updated = {
      ...ujianPaymentConfig,
      qrBankName: editQrBankName,
      qrAccountName: editQrAccountName,
      qrAccountNo: editQrAccountNo,
      qrNotes: editQrNotes
    };
    setUjianPaymentConfig(updated);
    localStorage.setItem('asmm_ujian_payment_config', JSON.stringify(updated));
    window.dispatchEvent(new Event('asmm_db_updated'));
    setIsQrModalOpen(false);
    showToast('success', 'Butiran Bank / QR Dikemaskini', 'Maklumat DuitNow QR telah berjaya disimpan.');
  };

  const isManagement = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari', 'Ketua Gelanggang']));

  const handleOpenCreate = () => {
    setModalMode('create');
    setSelectedNoticeId(null);
    setTitle('');
    setContent('');
    setType('acara');
    setTargetGelanggang('Semua Gelanggang');
    setEventDate('');
    setEventEndDate('');
    setIsMultiDay(false);
    setEventTime('');
    setEventLocation('');
    setRegistrationDeadline('');
    setCtaButtonText('');
    setHasFamilyAddon(false);
    setFreeSeatsPerPax(2);
    setFreeMealsPerPax(2);
    setSeatOriginalPrice(37);
    setSeatPromoPrice(17);
    setMealOriginalPrice(18);
    setMealPromoPrice(13);
    setPromoDeadline('2026-11-30');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (notice: NoticeItem) => {
    setModalMode('edit');
    setSelectedNoticeId(notice.id);
    setTitle(notice.title);
    setContent(notice.content);
    setType(notice.type);
    setTargetGelanggang(notice.targetGelanggang);
    setEventDate(notice.eventDate || '');
    setEventEndDate(notice.eventEndDate || '');
    setIsMultiDay(Boolean(notice.eventEndDate && notice.eventEndDate !== notice.eventDate));
    setEventTime(notice.eventTime || '');
    setEventLocation(notice.eventLocation || '');
    setRegistrationDeadline(notice.registrationDeadline || '');
    setCtaButtonText(notice.ctaButtonText || '');
    setCtaButtonLink(notice.ctaButtonLink || '/payments');
    setPollOptionsInput(notice.pollOptions?.map(o => o.text).join(', ') || 'Option 1, Option 2, Option 3');
    setHasFamilyAddon(Boolean(notice.hasFamilyAddon || notice.title.toLowerCase().includes('ujian') || notice.title.toLowerCase().includes('bengkung')));
    setFreeSeatsPerPax(notice.freeSeatsPerPax ?? 2);
    setFreeMealsPerPax(notice.freeMealsPerPax ?? 2);
    setSeatOriginalPrice(notice.seatOriginalPrice ?? 37);
    setSeatPromoPrice(notice.seatPromoPrice ?? 17);
    setMealOriginalPrice(notice.mealOriginalPrice ?? 18);
    setMealPromoPrice(notice.mealPromoPrice ?? 13);
    setPromoDeadline(notice.promoDeadline || '2026-11-30');
    setIsModalOpen(true);
  };

  const handleSaveNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      showToast('error', 'Sila isi Tajuk dan Kandungan Notis');
      return;
    }

    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;

    if (modalMode === 'create') {
      const newNotice: NoticeItem = {
        id: `notice-${Date.now()}`,
        title,
        content,
        type,
        authorName: currentUser?.name || 'Pentadbir ASMM',
        authorRole: (currentUser?.role as Role) || 'Pengerusi',
        targetGelanggang,
        createdAt: new Date().toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }),
        eventDate: (type === 'acara' || type === 'Peringatan Latihan') ? eventDate : undefined,
        eventEndDate: (type === 'acara' || type === 'Peringatan Latihan') ? ((isMultiDay && eventEndDate) ? eventEndDate : undefined) : undefined,
        eventTime: (type === 'acara' || type === 'Peringatan Latihan') ? eventTime : undefined,
        eventLocation: type === 'acara' ? eventLocation : undefined,
        registrationDeadline: type === 'acara' ? registrationDeadline : undefined,
        actionConfirm: type === 'Peringatan Latihan' ? 'Hadir 🔥' : undefined,
        actionDecline: type === 'Peringatan Latihan' ? 'Cuti / Uzur 🤒' : undefined,
        ctaButtonText: type === 'pengumuman' ? ctaButtonText : undefined,
        ctaButtonLink: type === 'pengumuman' ? ctaButtonLink : undefined,
        hasFamilyAddon: type === 'acara' ? hasFamilyAddon : undefined,
        freeSeatsPerPax: type === 'acara' && hasFamilyAddon ? Number(freeSeatsPerPax) : undefined,
        freeMealsPerPax: type === 'acara' && hasFamilyAddon ? Number(freeMealsPerPax) : undefined,
        seatOriginalPrice: type === 'acara' && hasFamilyAddon ? Number(seatOriginalPrice) : undefined,
        seatPromoPrice: type === 'acara' && hasFamilyAddon ? Number(seatPromoPrice) : undefined,
        mealOriginalPrice: type === 'acara' && hasFamilyAddon ? Number(mealOriginalPrice) : undefined,
        mealPromoPrice: type === 'acara' && hasFamilyAddon ? Number(mealPromoPrice) : undefined,
        promoDeadline: type === 'acara' && hasFamilyAddon ? promoDeadline : undefined,
        pollOptions: type === 'poll'
          ? pollOptionsInput.split(',').map((opt, idx) => ({ id: `opt-${idx + 1}`, text: opt.trim(), count: 0 }))
          : undefined,
        rsvps: (type === 'acara' || type === 'Peringatan Latihan') ? [] : undefined,
        pollVotes: type === 'poll' ? [] : undefined
      };

      if (hasFamilyAddon || title.toLowerCase().includes('ujian') || title.toLowerCase().includes('bengkung')) {
        localStorage.setItem('asmm_ujian_pricing_config', JSON.stringify({
          freeSeatsPerPax: Number(freeSeatsPerPax),
          freeMealsPerPax: Number(freeMealsPerPax),
          seatOriginalPrice: Number(seatOriginalPrice),
          seatPromoPrice: Number(seatPromoPrice),
          mealOriginalPrice: Number(mealOriginalPrice),
          mealPromoPrice: Number(mealPromoPrice),
          promoDeadline,
          registrationDeadline,
          eventDate,
          eventEndDate: isMultiDay ? eventEndDate : '',
          eventTime,
          eventLocation
        }));
        window.dispatchEvent(new Event('asmm_db_updated'));
      }

      const updated = await saveNoticeDB(newNotice);
      setNotices(updated);
      showToast('success', 'Notis Berjaya Dicipta!', `Pengumuman "${title}" telah diterbitkan.`);
    } else if (modalMode === 'edit' && selectedNoticeId) {
      // Edit existing notice
      const existing = notices.find(n => n.id === selectedNoticeId);
      if (existing) {
        const editedNotice: NoticeItem = {
          ...existing,
          title,
          content,
          type,
          targetGelanggang,
          eventDate: (type === 'acara' || type === 'Peringatan Latihan') ? eventDate : undefined,
          eventEndDate: (type === 'acara' || type === 'Peringatan Latihan') ? ((isMultiDay && eventEndDate) ? eventEndDate : undefined) : undefined,
          eventTime: (type === 'acara' || type === 'Peringatan Latihan') ? eventTime : undefined,
          eventLocation: type === 'acara' ? eventLocation : undefined,
          registrationDeadline: type === 'acara' ? registrationDeadline : undefined,
          actionConfirm: type === 'Peringatan Latihan' ? (existing.actionConfirm || 'Hadir 🔥') : undefined,
          actionDecline: type === 'Peringatan Latihan' ? (existing.actionDecline || 'Cuti / Uzur 🤒') : undefined,
          ctaButtonText: type === 'pengumuman' ? ctaButtonText : undefined,
          ctaButtonLink: type === 'pengumuman' ? ctaButtonLink : undefined,
          hasFamilyAddon: type === 'acara' ? hasFamilyAddon : undefined,
          freeSeatsPerPax: type === 'acara' && hasFamilyAddon ? Number(freeSeatsPerPax) : undefined,
          freeMealsPerPax: type === 'acara' && hasFamilyAddon ? Number(freeMealsPerPax) : undefined,
          seatOriginalPrice: type === 'acara' && hasFamilyAddon ? Number(seatOriginalPrice) : undefined,
          seatPromoPrice: type === 'acara' && hasFamilyAddon ? Number(seatPromoPrice) : undefined,
          mealOriginalPrice: type === 'acara' && hasFamilyAddon ? Number(mealOriginalPrice) : undefined,
          mealPromoPrice: type === 'acara' && hasFamilyAddon ? Number(mealPromoPrice) : undefined,
          promoDeadline: type === 'acara' && hasFamilyAddon ? promoDeadline : undefined,
          pollOptions: type === 'poll'
            ? pollOptionsInput.split(',').map((opt, idx) => {
              const trimmed = opt.trim();
              const existingOpt = existing.pollOptions?.find(o => o.text === trimmed);
              return { id: existingOpt?.id || `opt-${idx + 1}`, text: trimmed, count: existingOpt?.count || 0 };
            })
            : undefined,
        };

        if (hasFamilyAddon || title.toLowerCase().includes('ujian') || title.toLowerCase().includes('bengkung')) {
          localStorage.setItem('asmm_ujian_pricing_config', JSON.stringify({
            freeSeatsPerPax: Number(freeSeatsPerPax),
            freeMealsPerPax: Number(freeMealsPerPax),
            seatOriginalPrice: Number(seatOriginalPrice),
            seatPromoPrice: Number(seatPromoPrice),
            mealOriginalPrice: Number(mealOriginalPrice),
            mealPromoPrice: Number(mealPromoPrice),
            promoDeadline,
            registrationDeadline,
            eventDate,
            eventEndDate: isMultiDay ? eventEndDate : '',
            eventTime,
            eventLocation
          }));
          window.dispatchEvent(new Event('asmm_db_updated'));
        }

        const updated = await saveNoticeDB(editedNotice);
        setNotices(updated);
        showToast('success', 'Notis Dikemaskini!', `"${title}" telah disimpan.`);
      }
    }

    setIsModalOpen(false);
    requestAnimationFrame(() => {
      window.scrollTo({ top: currentScrollY, left: currentScrollX, behavior: 'instant' });
    });
  };

  const [noticeToDelete, setNoticeToDelete] = useState<NoticeItem | null>(null);
  const [isDeletingNotice, setIsDeletingNotice] = useState<boolean>(false);

  const handleConfirmDeleteNotice = async () => {
    if (!noticeToDelete) return;
    const id = noticeToDelete.id;
    const deletedTitle = noticeToDelete.title;
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    setIsDeletingNotice(true);
    try {
      // 1. Optimistic state update via AJAX without reload
      setNotices(prev => prev.filter(n => n.id !== id));

      // 2. Persistent deletion in DB & aaPanel MySQL
      await deleteNoticeDB(id);

      showToast('success', 'Notis Telah Dipadam', `Notis "${deletedTitle}" telah berjaya dipadam.`);

      // 3. If currently in single notice view for this notice, return to list
      if (selectedNoticeIdForView === id) {
        navigateToNotice(null);
      }
    } catch (err) {
      console.error("Failed to delete notice:", err);
      showToast('error', 'Ralat', 'Gagal memadam notis. Sila cuba lagi.');
    } finally {
      setIsDeletingNotice(false);
      setNoticeToDelete(null);
      requestAnimationFrame(() => {
        window.scrollTo({ top: currentScrollY, left: currentScrollX, behavior: 'instant' });
      });
    }
  };

  const handleRsvpSubmit = async (
    noticeId: string,
    status: 'Hadir' | 'Tidak Hadir' | 'Ragu-ragu',
    memberId?: string,
    memberName?: string,
    memberRole?: Role,
    memberGelanggang?: string,
    reason?: string
  ) => {
    if (!currentUser) return;

    const targetId = memberId || currentUser.id;
    const targetName = memberName || currentUser.name;
    const targetRole = memberRole || getHighestRole(currentUser);
    const targetGelanggang = memberGelanggang || currentUser.gelanggang;

    const updated = notices.map(n => {
      if (n.id !== noticeId) return n;
      const existingRsvps = n.rsvps || [];
      const filtered = existingRsvps.filter(r => r.userId !== targetId);
      const newRsvp = {
        userId: targetId,
        userName: targetName,
        role: targetRole,
        gelanggang: targetGelanggang,
        status,
        reason,
        updatedAt: new Date().toLocaleString('ms-MY')
      };
      return { ...n, rsvps: [...filtered, newRsvp] };
    });

    setNotices(updated);
    await updateNoticeDB(updated);

    // Sync directly to aaPanel practice_rsvps table
    const targetNotice = notices.find(n => n.id === noticeId);
    recordPracticeRsvpDB({
      id: `rsvp-${targetId}-${noticeId}`,
      memberId: targetId,
      memberName: targetName,
      gelanggang: targetGelanggang || 'Semua Gelanggang',
      sessionDate: targetNotice?.eventDate || new Date().toISOString().split('T')[0],
      sessionTime: targetNotice?.eventTime || '',
      status: status === 'Tidak Hadir' ? 'Uzur' : status,
      notes: reason || ''
    }).catch(() => { });

    showToast('success', 'RSVP Berjaya Disimpan', `Status Kehadiran ${targetId === currentUser.id ? '' : `${targetName} `}: ${status}`);
  };

  const handleVotePoll = async (noticeId: string, optionId: string) => {
    if (!currentUser) return;

    const updated = notices.map(n => {
      if (n.id !== noticeId) return n;
      const opts = n.pollOptions || [];
      const updatedOpts = opts.map(o => o.id === optionId ? { ...o, count: o.count + 1 } : o);

      return {
        ...n,
        pollOptions: updatedOpts,
        pollVotes: [...(n.pollVotes || []), { userId: currentUser.id, userName: currentUser.name, optionId, updatedAt: new Date().toISOString() }]
      };
    });

    setNotices(updated);
    await updateNoticeDB(updated);
    showToast('success', 'Undian Diterima!', 'Terima kasih atas maklum balas anda.');
  };

  const selectedNotice = React.useMemo(() => {
    return notices.find(n => n.id === selectedNoticeIdForView);
  }, [notices, selectedNoticeIdForView]);

  const activeUjianNotice = React.useMemo(() => {
    return notices.find(n => n.hasFamilyAddon || n.title.toLowerCase().includes('ujian') || n.title.toLowerCase().includes('bengkung'));
  }, [notices]);

  const isPracticeNotice = (n: NoticeItem) => {
    return (
      n.type === 'Peringatan Latihan' ||
      n.type === 'latihan' ||
      Boolean(n.actionConfirm) ||
      n.title.toLowerCase().includes('peringatan gelanggang')
    );
  };

  const activePracticeNotice = React.useMemo(() => {
    return notices.find(n => isPracticeNotice(n));
  }, [notices]);

  const totalRsvps = selectedNotice?.rsvps?.length || 0;

  const filteredNotices = notices.filter(n => {
    if (filterType === 'all') return true;
    if (filterType === 'latihan') {
      return isPracticeNotice(n);
    }
    return n.type === filterType;
  });

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {selectedNoticeIdForView ? (
        selectedNotice ? (
          <div className="space-y-6">
            {/* Breadcrumb / Back button */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateToNotice(null)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer text-slate-600 border-none bg-transparent flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="text-xs text-slate-400 font-bold cursor-pointer hover:underline" onClick={() => navigateToNotice(null)}>Notis Board</span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-400 font-bold uppercase">{selectedNotice.type}</span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-800 font-extrabold truncate max-w-[200px]">{selectedNotice.title}</span>
            </div>

            {/* Layout split view */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs font-sans text-xs">
              {/* Left Column: Notice Detailed Information */}
              <div className="lg:col-span-7 space-y-6 border-b lg:border-b-0 lg:border-r border-slate-100 pb-6 lg:pb-0 lg:pr-8">
                <div className="space-y-3">
                  <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${selectedNotice.type === 'acara'
                    ? 'bg-red-50 text-asmm-red border-red-200'
                    : selectedNotice.type === 'pengumuman'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}>
                    {selectedNotice.type === 'acara' ? 'Acara / Latihan (RSVP)' : selectedNotice.type === 'pengumuman' ? 'Pengumuman' : 'Undian (Poll)'}
                  </span>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-snug">{selectedNotice.title}</h2>
                  <div className="text-[10px] text-slate-400 font-bold flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span>Diterbitkan pada: {selectedNotice.createdAt}</span>
                    <span>•</span>
                    <span>Oleh: <strong className="text-slate-700">{selectedNotice.authorName}</strong> ({selectedNotice.authorRole})</span>
                  </div>
                </div>

                {/* Content description */}
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Butiran Notis</h4>
                  <p className="text-xs text-slate-700 leading-relaxed font-medium whitespace-pre-line bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                    {selectedNotice.content}
                  </p>
                </div>

                {/* Event Specific Location/Calendar Box */}
                {selectedNotice.type === 'acara' && (
                  <div className="space-y-3 pt-2">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Butiran Acara</h4>
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100/50 rounded-xl text-asmm-red">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Tarikh & Masa</div>
                          <div className="text-xs font-extrabold text-slate-800">{formatEventDatesFull(selectedNotice.eventDate, selectedNotice.eventEndDate)} | {selectedNotice.eventTime}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
                        <div className="p-2 bg-blue-100/50 rounded-xl text-blue-600">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400 font-bold uppercase">Lokasi Gelanggang / Dewan</div>
                          <div className="text-xs font-extrabold text-slate-800">{selectedNotice.eventLocation}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ujian Bengkung Family Single Page Action Card */}
                {(selectedNotice.hasFamilyAddon ||
                  selectedNotice.title.toLowerCase().includes('ujian') ||
                  selectedNotice.title.toLowerCase().includes('bengkung') ||
                  selectedNotice.content.toLowerCase().includes('bengkung') ||
                  selectedNotice.ctaButtonLink?.includes('ujian')) && (
                    <div className="p-5 sm:p-6 bg-white border border-slate-200 rounded-3xl shadow-sm space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-white bg-red-600 px-3 py-0.5 rounded-full shadow-2xs">
                              <Award className="w-3 h-3" />
                              <span>Pakej Ujian & Kemudahan Percuma</span>
                            </span>
                            <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
                              Sah: {selectedNotice.promoDeadline ? new Date(selectedNotice.promoDeadline).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '30 Nov 2026'}
                            </span>
                          </div>
                          <h4 className="text-base font-black text-slate-900">
                            Pendaftaran Calon & Tempahan Tambahan Keluarga
                          </h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            window.history.pushState(null, '', '/ujian-bengkung');
                            window.dispatchEvent(new PopStateEvent('popstate'));
                          }}
                          className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-full transition shadow-sm flex items-center justify-center gap-2 shrink-0 cursor-pointer self-start sm:self-auto"
                        >
                          <span>Buka Halaman Ujian</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-red-600 shrink-0 shadow-2xs">
                            <Armchair className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Percuma Setiap Calon</span>
                            <span className="font-extrabold text-slate-900 block truncate">
                              {selectedNotice.freeSeatsPerPax ?? 2} Kerusi + {selectedNotice.freeMealsPerPax ?? 2} Pek
                            </span>
                          </div>
                        </div>

                        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                            <Sparkles className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Kerusi Tambahan</span>
                            <span className="font-extrabold text-slate-900 block truncate">
                              RM {selectedNotice.seatPromoPrice ?? 17} <span className="text-[10px] text-slate-400 font-semibold line-through">RM {selectedNotice.seatOriginalPrice ?? 37}</span>
                            </span>
                          </div>
                        </div>

                        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                            <Utensils className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Makanan Tambahan</span>
                            <span className="font-extrabold text-slate-900 block truncate">
                              RM {selectedNotice.mealPromoPrice ?? 13} <span className="text-[10px] text-slate-400 font-semibold line-through">RM {selectedNotice.mealOriginalPrice ?? 18}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Target Audience Metadata */}
                <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-[11px] text-slate-500">
                  <div>Kumpulan Sasaran: <strong className="text-asmm-red font-extrabold">{selectedNotice.targetGelanggang}</strong></div>
                  {isManagement && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(selectedNotice)}
                        className="text-[10px] font-extrabold text-slate-600 hover:text-blue-600 py-1.5 px-3.5 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-xl transition-all flex items-center gap-1 cursor-pointer border-solid"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        <span>Kemaskini</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setNoticeToDelete(selectedNotice)}
                        className="text-[10px] font-extrabold text-slate-600 hover:text-rose-600 py-1.5 px-3 bg-slate-50 hover:bg-rose-50 border border-slate-200 hover:border-rose-200 rounded-xl transition-all cursor-pointer border-solid flex items-center gap-1.5"
                        title="Padam Notis"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                        <span>Padam</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Interaction Panel (RSVP or Poll voting) */}
              <div className="lg:col-span-5 space-y-6">
                {(selectedNotice.type === 'acara' || selectedNotice.type === 'Peringatan Latihan' || Boolean(selectedNotice.actionConfirm)) && (
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-slate-100">
                      <h3 className="text-sm font-extrabold text-slate-900">Sahkan Kehadiran (RSVP)</h3>
                      <p className="text-[10px] text-slate-500 mt-0.5">Sila sahkan maklum balas kehadiran bagi melancarkan persiapan gelanggang & persatuan.</p>
                    </div>

                    {/* RSVP Kehadiran Anda */}
                    <div className="space-y-2 pb-4 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-800">Kehadiran Anda:</span>
                        {(() => {
                          const mainRsvp = selectedNotice.rsvps?.find(r => r.userId === currentUser?.id);
                          return mainRsvp ? (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${mainRsvp.status === 'Hadir'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-205'
                              : mainRsvp.status === 'Tidak Hadir'
                                ? 'bg-rose-50 text-rose-800 border-rose-205'
                                : 'bg-amber-50 text-amber-800 border-amber-205'
                              }`}>
                              {mainRsvp.status}
                            </span>
                          ) : (
                            <span className="text-[10px] text-amber-700 font-bold">Belum Pengesahan</span>
                          );
                        })()}
                      </div>

                      <div className={`grid gap-2 ${selectedNotice.type === 'Peringatan Latihan' || selectedNotice.actionConfirm ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {(() => {
                          const mainRsvp = selectedNotice.rsvps?.find(r => r.userId === currentUser?.id);
                          const isPractice = selectedNotice.type === 'Peringatan Latihan' || Boolean(selectedNotice.actionConfirm);
                          const confirmText = selectedNotice.actionConfirm || 'Hadir 🔥';
                          const declineText = selectedNotice.actionDecline || 'Cuti / Uzur 🤒';
                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => handleRsvpSubmit(selectedNotice.id, 'Hadir')}
                                className={`py-2 px-3 text-[11px] font-extrabold rounded-xl border transition-all flex items-center justify-center cursor-pointer ${mainRsvp?.status === 'Hadir'
                                  ? 'bg-asmm-red text-white border-asmm-red shadow-xs ring-2 ring-red-400'
                                  : 'bg-white text-asmm-red border-red-200 hover:bg-red-50'
                                  }`}
                              >
                                {confirmText}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRsvpSubmit(selectedNotice.id, 'Tidak Hadir')}
                                className={`py-2 px-3 text-[11px] font-extrabold rounded-xl border transition-all flex items-center justify-center cursor-pointer ${mainRsvp?.status === 'Tidak Hadir'
                                  ? 'bg-slate-800 text-white border-slate-800 shadow-xs ring-2 ring-slate-400'
                                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                              >
                                {declineText}
                              </button>
                              {!isPractice && (
                                <button
                                  type="button"
                                  onClick={() => handleRsvpSubmit(selectedNotice.id, 'Ragu-ragu')}
                                  className={`py-2 text-[11px] font-extrabold rounded-xl border transition-all flex items-center justify-center cursor-pointer ${mainRsvp?.status === 'Ragu-ragu'
                                    ? 'bg-amber-600 text-white border-amber-600 shadow-xs ring-2 ring-amber-400'
                                    : 'bg-white text-amber-700 border-slate-200 hover:bg-amber-50'
                                    }`}
                                >
                                  Ragu
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* RSVP Kehadiran Ahli Keluarga */}
                    {canUserAddChild(currentUser) && (
                      <div className="space-y-3 pb-4 border-b border-slate-100">
                        <span className="text-[11px] font-bold text-slate-800 block">Kehadiran Ahli Keluarga / Tanggungan:</span>
                        {myChildren.length === 0 ? (
                          <p className="text-[10px] text-slate-500 italic bg-slate-50 p-3 rounded-xl border border-slate-200">
                            Sila hubungkan profil ahli keluarga anda di dashboard untuk RSVP mereka.
                          </p>
                        ) : (
                          <div className="space-y-3.5">
                            {myChildren.map(child => {
                              const childRsvp = selectedNotice.rsvps?.find(r => r.userId === child.id);
                              const isPractice = selectedNotice.type === 'Peringatan Latihan' || Boolean(selectedNotice.actionConfirm);
                              const confirmText = selectedNotice.actionConfirm || 'Hadir 🔥';
                              const declineText = selectedNotice.actionDecline || 'Cuti / Uzur 🤒';
                              return (
                                <div key={child.id} className="p-3 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-black text-slate-800 truncate max-w-[150px]">{child.name}</span>
                                    {childRsvp ? (
                                      <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded border ${childRsvp.status === 'Hadir'
                                        ? 'bg-emerald-50 text-emerald-800 border-emerald-250'
                                        : childRsvp.status === 'Tidak Hadir'
                                          ? 'bg-rose-50 text-rose-800 border-rose-250'
                                          : 'bg-amber-50 text-amber-800 border-amber-250'
                                        }`}>
                                        {childRsvp.status}
                                      </span>
                                    ) : (
                                      <span className="text-[9px] font-bold text-slate-400">Tiada Respon</span>
                                    )}
                                  </div>
                                  <div className={`grid gap-1.5 ${isPractice ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                    <button
                                      type="button"
                                      onClick={() => handleRsvpSubmit(selectedNotice.id, 'Hadir', child.id, child.name, child.role, child.gelanggang)}
                                      className={`py-1.5 px-2 text-[10px] font-bold rounded-lg border transition-all flex items-center justify-center cursor-pointer ${childRsvp?.status === 'Hadir'
                                        ? 'bg-asmm-red text-white border-asmm-red'
                                        : 'bg-white text-asmm-red border-red-200 hover:bg-red-50'
                                        }`}
                                    >
                                      {confirmText}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRsvpSubmit(selectedNotice.id, 'Tidak Hadir', child.id, child.name, child.role, child.gelanggang)}
                                      className={`py-1.5 px-2 text-[10px] font-bold rounded-lg border transition-all flex items-center justify-center cursor-pointer ${childRsvp?.status === 'Tidak Hadir'
                                        ? 'bg-slate-800 text-white border-slate-800'
                                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                        }`}
                                    >
                                      {declineText}
                                    </button>
                                    {!isPractice && (
                                      <button
                                        type="button"
                                        onClick={() => handleRsvpSubmit(selectedNotice.id, 'Ragu-ragu', child.id, child.name, child.role, child.gelanggang)}
                                        className={`py-1.5 text-[10px] font-bold rounded-lg border transition-all flex items-center justify-center cursor-pointer ${childRsvp?.status === 'Ragu-ragu'
                                          ? 'bg-amber-600 text-white border-amber-600'
                                          : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'
                                          }`}
                                      >
                                        Ragu
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* RSVP Stats Breakdown trigger */}
                    <button
                      type="button"
                      onClick={() => setBreakdownNotice(selectedNotice)}
                      className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-extrabold rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer border-none"
                    >
                      <BarChart3 className="w-3.5 h-3.5 text-amber-400" />
                      <span>Pecahan Kehadiran Ahli ({totalRsvps})</span>
                    </button>
                  </div>
                )}

                {/* Poll Controls */}
                {selectedNotice.type === 'poll' && (
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-slate-100">
                      <h3 className="text-sm font-extrabold text-slate-900">Undian Notis (Poll)</h3>
                      <p className="text-[10px] text-slate-500 mt-0.5">Maklum balas anda penting untuk perbincangan usul persatuan.</p>
                    </div>

                    <div className="space-y-2">
                      {selectedNotice.pollOptions?.map((opt) => {
                        const totalVotes = selectedNotice.pollOptions?.reduce((a, b) => a + b.count, 0) || 1;
                        const pct = Math.round((opt.count / totalVotes) * 100);
                        return (
                          <button
                            key={opt.id}
                            onClick={() => handleVotePoll(selectedNotice.id, opt.id)}
                            className="w-full p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-left transition-all flex flex-col gap-1 cursor-pointer"
                          >
                            <div className="w-full flex items-center justify-between font-bold text-slate-800 text-xs">
                              <span>{opt.text}</span>
                              <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-slate-200">
                                {opt.count} undi ({pct}%)
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden mt-1">
                              <div className="bg-asmm-red h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => setBreakdownNotice(selectedNotice)}
                      className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-extrabold rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer border-none"
                    >
                      <BarChart3 className="w-3.5 h-3.5 text-amber-400" />
                      <span>Pecahan Undian Notis</span>
                    </button>
                  </div>
                )}

                {/* Announcement CTA */}
                {selectedNotice.type === 'pengumuman' && (
                  <div className="space-y-4 bg-slate-50 border border-slate-200 p-5 rounded-3xl">
                    <div>
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Tindakan Digalakkan</h3>
                      <p className="text-[10px] text-slate-500 mt-0.5">Sila layari pautan rasmi di bawah untuk maklumat pendaftaran, pembayaran, atau urusan perguruan.</p>
                    </div>
                    <a
                      href={selectedNotice.ctaButtonLink || '#'}
                      className="w-full py-2.5 bg-asmm-red text-white text-center font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all shadow-sm flex items-center justify-center gap-1 cursor-pointer no-underline border-none"
                    >
                      <span>{selectedNotice.ctaButtonText || 'Tindakan Seterusnya'}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-3xl border border-slate-200 text-center space-y-4">
            <p className="text-sm font-extrabold text-slate-800">Notis atau Acara tidak dijumpai.</p>
            <button
              onClick={() => navigateToNotice(null)}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all"
            >
              Kembali ke Notis Board
            </button>
          </div>
        )
      ) : (
        <>
          {/* Fixed Header Banner */}
          <PageHeader
            badgeText="Komunikasi"
            badgeIcon={MessageSquare}
            title="Pengurusan Notis"
          >
            {isManagement && (
              <button
                onClick={handleOpenCreate}
                className="px-4 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none"
              >
                <Plus className="w-4 h-4" />
                Cipta Notis
              </button>
            )}
          </PageHeader>

          {/* Integrated Ujian Bengkung Family Notice Banner */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 space-y-5 transition-all hover:border-slate-300">
            {/* Top Row: Title, Subtitle, Badges & Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-start gap-4 sm:gap-5 flex-1 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 text-red-600 flex items-center justify-center shrink-0 shadow-2xs">
                  <Award className="w-7 h-7" />
                </div>
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-3 py-0.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider rounded-full shadow-2xs">
                      Pendaftaran Dibuka
                    </span>
                    <span className="px-2.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200/70 text-[10px] font-bold rounded-full">
                      Promosi Sah Sehingga: {activeUjianNotice?.promoDeadline ? new Date(activeUjianNotice.promoDeadline).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '30 Nov 2026'}
                    </span>
                    {activeUjianNotice?.registrationDeadline && (
                      <span className="px-2.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200/70 text-[10px] font-bold rounded-full">
                        Tutup Pendaftaran: {new Date(activeUjianNotice.registrationDeadline).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {activeUjianNotice?.eventDate && (
                      <span className="px-2.5 py-0.5 bg-slate-100 text-slate-800 border border-slate-200 text-[10px] font-bold rounded-full">
                        Acara: {formatEventDates(activeUjianNotice.eventDate, activeUjianNotice.eventEndDate)}{activeUjianNotice.eventTime ? ` (${activeUjianNotice.eventTime})` : ''}
                      </span>
                    )}
                  </div>
                  <h3 className="text-base sm:text-xl font-black text-slate-900 tracking-tight leading-snug">
                    Pendaftaran Ujian Kenaikan Bengkung & Tempahan Kemudahan Keluarga
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-2xl">
                    Kalkulator pakej keluarga berpusat: Setiap calon automatik menerima <strong>{activeUjianNotice?.freeSeatsPerPax ?? 2} Tempat Duduk Percuma</strong> & <strong>{activeUjianNotice?.freeMealsPerPax ?? 2} Pek Makanan Percuma</strong>. Lokasi: <strong>{activeUjianNotice?.eventLocation || 'Pusat Riadah Wadi AnNour'}</strong>.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 shrink-0 self-start lg:self-center w-full lg:w-auto pt-2 lg:pt-0">
                {isManagement && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeUjianNotice) {
                          handleOpenEdit(activeUjianNotice);
                        } else {
                          handleOpenCreate();
                          setTitle('Pendaftaran Ujian Kenaikan Bengkung & Tempahan Kemudahan Keluarga');
                          setContent(`Assalamualaikum w.b.t. dan Salam Sejahtera.\n\nPendaftaran bagi Ujian Kenaikan Bengkung kini dibuka kepada semua pelatih yang layak.\n\nMAKLUMAT PAKEJ PENDAFTARAN CALON:\nSetiap bayaran yuran pendaftaran bengkung telah merangkumi kemudahan percuma untuk ahli keluarga seperti berikut:\n2 Tempat Duduk - Percuma\n2 Pek Makanan - Percuma\n\nTEMPAHAN TAMBAHAN (ADD-ON):\nBagi ibu bapa atau waris yang ingin membawa ahli keluarga melebihi kuota percuma yang disediakan, tempahan tempat duduk dan makanan tambahan hendaklah dibuat secara berasingan mengikut ketetapan kadar berikut:\n\nTempat Duduk Tambahan:\nHarga Asal: RM 37 setiap kerusi\nHarga Promosi Awalan: RM 17 setiap kerusi\n\nMakanan Tambahan:\nHarga Asal: RM 18 setiap pax\nHarga Promosi Awalan: RM 13 setiap pax\n\nPENTING:\nKadar diskaun dan harga promosi awalan ini hanya sah untuk pembelian dan pengesahan yang dibuat sebelum atau pada 30 November 2026 sahaja. Selepas tarikh tersebut, semua pembelian tambahan akan dikenakan pada harga asal.\n\nLangkah ini diambil bagi memastikan susun atur kerusi di dewan mencukupi serta urusan tempahan katering berjalan lancar dan teratur.\n\nSila buat pendaftaran dan pengesahan kehadiran ahli keluarga tambahan kepada pihak pengurusan gelanggang dengan kadar segera.\n\nSekian, terima kasih.`);
                          setEventDate('2026-12-11');
                          setEventEndDate('2026-12-12');
                          setIsMultiDay(true);
                          setEventTime('8.00 Pagi - 2.00 Petang');
                          setEventLocation('Pusat Riadah Wadi AnNour');
                          setRegistrationDeadline('2026-12-10');
                          setHasFamilyAddon(true);
                          setFreeSeatsPerPax(2);
                          setFreeMealsPerPax(2);
                          setSeatOriginalPrice(37);
                          setSeatPromoPrice(17);
                          setMealOriginalPrice(18);
                          setMealPromoPrice(13);
                          setPromoDeadline('2026-11-30');
                        }
                      }}
                      className="flex-1 sm:flex-initial px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-full transition cursor-pointer flex items-center justify-center gap-1.5 shadow-sm"
                      title="Edit butiran kerusi, makanan, kadar promosi/penuh, tarikh & lokasi"
                    >
                      <Edit3 className="w-3.5 h-3.5 text-amber-400" />
                      <span>{activeUjianNotice ? 'Edit Butiran Acara' : 'Cipta & Edit Butiran'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleOpenCreate();
                        setTitle('Pendaftaran Ujian Kenaikan Bengkung & Tempahan Kemudahan Keluarga');
                        setContent(`Assalamualaikum w.b.t. dan Salam Sejahtera.\n\nPendaftaran bagi Ujian Kenaikan Bengkung kini dibuka kepada semua pelatih yang layak.\n\nMAKLUMAT PAKEJ PENDAFTARAN CALON:\nSetiap bayaran yuran pendaftaran bengkung telah merangkumi kemudahan percuma untuk ahli keluarga seperti berikut:\n2 Tempat Duduk - Percuma\n2 Pek Makanan - Percuma\n\nTEMPAHAN TAMBAHAN (ADD-ON):\nBagi ibu bapa atau waris yang ingin membawa ahli keluarga melebihi kuota percuma yang disediakan, tempahan tempat duduk dan makanan tambahan hendaklah dibuat secara berasingan mengikut ketetapan kadar berikut:\n\nTempat Duduk Tambahan:\nHarga Asal: RM 37 setiap kerusi\nHarga Promosi Awalan: RM 17 setiap kerusi\n\nMakanan Tambahan:\nHarga Asal: RM 18 setiap pax\nHarga Promosi Awalan: RM 13 setiap pax\n\nPENTING:\nKadar diskaun dan harga promosi awalan ini hanya sah untuk pembelian dan pengesahan yang dibuat sebelum atau pada 30 November 2026 sahaja. Selepas tarikh tersebut, semua pembelian tambahan akan dikenakan pada harga asal.\n\nLangkah ini diambil bagi memastikan susun atur kerusi di dewan mencukupi serta urusan tempahan katering berjalan lancar dan teratur.\n\nSila buat pendaftaran dan pengesahan kehadiran ahli keluarga tambahan kepada pihak pengurusan gelanggang dengan kadar segera.\n\nSekian, terima kasih.`);
                        setEventDate(activeUjianNotice?.eventDate || '2026-12-11');
                        setEventEndDate(activeUjianNotice?.eventEndDate || '2026-12-12');
                        setIsMultiDay(Boolean((activeUjianNotice?.eventEndDate && activeUjianNotice.eventEndDate !== activeUjianNotice.eventDate) || !activeUjianNotice));
                        setEventTime(activeUjianNotice?.eventTime || '8.00 Pagi - 2.00 Petang');
                        setEventLocation(activeUjianNotice?.eventLocation || 'Pusat Riadah Wadi AnNour');
                        setRegistrationDeadline(activeUjianNotice?.registrationDeadline || '2026-12-10');
                        setTargetGelanggang('Semua Gelanggang');
                        setCtaButtonText('Daftar Sekarang');
                        setCtaButtonLink('/ujian-bengkung');
                      }}
                      className="flex-1 sm:flex-initial px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-full border border-slate-200 transition cursor-pointer"
                      title="Muat teks rasmi ini ke dalam borang notis baru"
                    >
                      <span>Guna Templat</span>
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    window.history.pushState(null, '', '/ujian-bengkung');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="flex-1 sm:flex-initial px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-bold rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Buka Halaman Ujian</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Middle Row: Floating Metric Strip (ASMM UI Hierarchy Standard) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
              <div className="p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-red-600 shrink-0 shadow-2xs">
                  <Armchair className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Percuma / Calon</span>
                  <span className="text-xs font-extrabold text-slate-900 truncate block">
                    {activeUjianNotice?.freeSeatsPerPax ?? 2} Kerusi Dewan
                  </span>
                </div>
              </div>

              <div className="p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                  <Utensils className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Percuma / Calon</span>
                  <span className="text-xs font-extrabold text-slate-900 truncate block">
                    {activeUjianNotice?.freeMealsPerPax ?? 2} Pek Makanan
                  </span>
                </div>
              </div>

              <div className="p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Kadar Promosi</span>
                  <span className="text-xs font-extrabold text-slate-900 truncate block">
                    RM {activeUjianNotice?.seatPromoPrice ?? 17} Seat • RM {activeUjianNotice?.mealPromoPrice ?? 13} Makan
                  </span>
                </div>
              </div>

              <div className="p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center text-slate-800 shrink-0 shadow-2xs">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Lokasi Majlis</span>
                  <span className="text-xs font-extrabold text-slate-900 truncate block" title={activeUjianNotice?.eventLocation || 'Dewan Latihan ASMM'}>
                    {activeUjianNotice?.eventLocation || 'Dewan Latihan ASMM'}
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom Row: Admin Quick Settings Strip */}
            {isManagement && (
              <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-600 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5 text-red-600" />
                    <span>Mod Bayaran (Admin):</span>
                  </span>
                  <div className="inline-flex p-0.5 bg-slate-100 rounded-full border border-slate-200/80">
                    <button
                      type="button"
                      onClick={() => handleUpdateUjianPaymentMethod('qr')}
                      className={`px-3 py-1 rounded-full text-[11px] font-extrabold transition-all cursor-pointer ${ujianPaymentConfig.paymentMethod === 'qr'
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900 bg-transparent'
                        }`}
                    >
                      DuitNow QR Sahaja
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateUjianPaymentMethod('fpx')}
                      className={`px-3 py-1 rounded-full text-[11px] font-extrabold transition-all cursor-pointer ${ujianPaymentConfig.paymentMethod === 'fpx'
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900 bg-transparent'
                        }`}
                    >
                      FPX Sahaja
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateUjianPaymentMethod('both')}
                      className={`px-3 py-1 rounded-full text-[11px] font-extrabold transition-all cursor-pointer ${ujianPaymentConfig.paymentMethod === 'both'
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900 bg-transparent'
                        }`}
                    >
                      FPX & QR
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setEditQrBankName(ujianPaymentConfig.qrBankName);
                    setEditQrAccountName(ujianPaymentConfig.qrAccountName);
                    setEditQrAccountNo(ujianPaymentConfig.qrAccountNo);
                    setEditQrNotes(ujianPaymentConfig.qrNotes);
                    setIsQrModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-full text-[11px] font-bold text-slate-700 transition cursor-pointer self-start sm:self-auto shadow-2xs"
                >
                  <Settings className="w-3.5 h-3.5 text-slate-500" />
                  <span>Urus Butiran Bank / QR</span>
                </button>
              </div>
            )}
          </div>

          {/* Featured PWA Practice Alert Card (Matching PWA Notification Toast design) */}
          {activePracticeNotice && filterType === 'all' && (
            <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-5 sm:p-6 space-y-3.5 relative overflow-hidden transition-all hover:border-slate-300">
              {/* Header: Logo + PSSASMM PWA + Timestamp */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0B0F19] overflow-hidden flex items-center justify-center p-0.5 border border-slate-800 shadow-2xs shrink-0">
                    <img src="/icons/icon-192.png" alt="PSSASMM" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xs font-black text-slate-800 tracking-wide flex items-center gap-2">
                    <span>PSSASMM PWA</span>
                    <span className="text-[9px] font-black uppercase px-2.5 py-0.5 rounded-full bg-red-50 text-asmm-red border border-red-200/80">
                      Notifikasi Sesi Latihan Gelanggang
                    </span>
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-slate-400">
                  Sekarang
                </span>
              </div>

              {/* Title & Body */}
              <div className="space-y-1 max-w-2xl">
                <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-snug">
                  {activePracticeNotice.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                  {activePracticeNotice.content}
                </p>
              </div>

              {/* Action Buttons: Hadir 🔥 vs Cuti / Uzur 🤒 */}
              {(() => {
                const userRsvp = activePracticeNotice.rsvps?.find(r => r.userId === currentUser?.id);
                const userStatus = userRsvp?.status;
                const hadirCount = activePracticeNotice.rsvps?.filter(r => r.status === 'Hadir').length || 0;
                const uzurCount = activePracticeNotice.rsvps?.filter(r => r.status === 'Tidak Hadir' || (r.status as string) === 'Uzur').length || 0;
                const confirmLabel = activePracticeNotice.actionConfirm || 'Hadir 🔥';
                const declineLabel = activePracticeNotice.actionDecline || 'Cuti / Uzur 🤒';

                return (
                  <div className="space-y-3 pt-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-lg">
                      <button
                        type="button"
                        onClick={() => handleRsvpSubmit(activePracticeNotice.id, 'Hadir')}
                        className={`py-2.5 px-5 rounded-2xl font-black text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer border-none shadow-xs ${userStatus === 'Hadir'
                          ? 'bg-emerald-600 text-white ring-2 ring-emerald-500 shadow-sm'
                          : 'bg-asmm-red hover:bg-asmm-crimson text-white active:scale-98'
                          }`}
                      >
                        {userStatus === 'Hadir' && <Check className="w-4 h-4 text-emerald-100" />}
                        <span>{userStatus === 'Hadir' ? '✓ Disahkan: Hadir 🔥' : confirmLabel}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleRsvpSubmit(activePracticeNotice.id, 'Tidak Hadir')}
                        className={`py-2.5 px-5 rounded-2xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${userStatus === 'Tidak Hadir'
                          ? 'bg-rose-600 text-white ring-2 ring-rose-500 shadow-sm'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-800 active:scale-98'
                          }`}
                      >
                        {userStatus === 'Tidak Hadir' && <Check className="w-4 h-4 text-rose-100" />}
                        <span>{userStatus === 'Tidak Hadir' ? '✓ Disahkan: Cuti / Uzur 🤒' : declineLabel}</span>
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 pt-2 border-t border-slate-100 font-medium">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200/60">
                          {hadirCount} Hadir
                        </span>
                        <span className="font-bold text-slate-600 bg-slate-50 px-2.5 py-0.5 rounded-full border border-slate-200/60">
                          {uzurCount} Cuti / Uzur
                        </span>
                        <span className="font-semibold text-slate-400">
                          • Gelanggang: <strong className="text-slate-700">{activePracticeNotice.targetGelanggang}</strong>
                        </span>
                      </div>

                      {canUserAddChild(currentUser) && myChildren.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-slate-600">RSVP Anak:</span>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {myChildren.map(child => {
                              const childRsvp = activePracticeNotice.rsvps?.find(r => r.userId === child.id);
                              return (
                                <div key={child.id} className="inline-flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-xl border border-slate-200 text-[10px]">
                                  <span className="font-bold text-slate-800">{child.name.split(' ')[0]}:</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRsvpSubmit(activePracticeNotice.id, 'Hadir', child.id, child.name, child.role, child.gelanggang)}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold border-none cursor-pointer ${childRsvp?.status === 'Hadir' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'
                                      }`}
                                  >
                                    Hadir
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRsvpSubmit(activePracticeNotice.id, 'Tidak Hadir', child.id, child.name, child.role, child.gelanggang)}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold border-none cursor-pointer ${childRsvp?.status === 'Tidak Hadir' ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-700'
                                      }`}
                                  >
                                    Uzur
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Filter Tabs & Layout Switcher */}
          <div className="flex items-center justify-between gap-3 bg-white p-3.5 rounded-3xl border border-slate-200 shadow-2xs">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap pb-0.5">
              {[
                { id: 'all', label: 'Semua' },
                { id: 'latihan', label: 'Peringatan Latihan (RSVP)' },
                { id: 'acara', label: 'Acara' },
                { id: 'pengumuman', label: 'Pengumuman' },
                { id: 'poll', label: 'Poll' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setFilterType(t.id)}
                  className={`px-4 py-2 rounded-2xl text-xs font-extrabold whitespace-nowrap shrink-0 transition-all border cursor-pointer ${filterType === t.id
                    ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border-slate-200'
                    }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Layout Switcher (Desktop only) */}
            <div className="hidden sm:flex items-center justify-end gap-2 shrink-0">
              <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-2xl border border-slate-200 w-fit shrink-0">
                <button
                  type="button"
                  onClick={() => setLayoutMode('list')}
                  className={`px-3 py-1.5 rounded-xl transition-all border-none cursor-pointer flex items-center gap-1.5 ${layoutMode === 'list'
                    ? 'bg-white text-slate-900 shadow-xs font-black'
                    : 'text-slate-500 hover:text-slate-800 bg-transparent font-bold'
                    }`}
                  title="List View"
                >
                  <List className="w-3.5 h-3.5" />
                  <span className="text-[10px]">Senarai</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLayoutMode('grid')}
                  className={`px-3 py-1.5 rounded-xl transition-all border-none cursor-pointer flex items-center gap-1.5 ${layoutMode === 'grid'
                    ? 'bg-white text-slate-900 shadow-xs font-black'
                    : 'text-slate-500 hover:text-slate-800 bg-transparent font-bold'
                    }`}
                  title="Grid View"
                >
                  <Grid className="w-3.5 h-3.5" />
                  <span className="text-[10px]">Grid</span>
                </button>
              </div>
            </div>
          </div>

          {/* Notices List/Grid */}
          <div className={effectiveLayout === 'list'
            ? "flex flex-col gap-4 w-full"
            : "grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6"
          }>
            {isLoading ? (
              <>
                <NoticeCardSkeleton layoutMode={effectiveLayout} />
                <NoticeCardSkeleton layoutMode={effectiveLayout} />
              </>
            ) : (
              filteredNotices.map((notice) => {
                if (isPracticeNotice(notice)) {
                  const userRsvp = notice.rsvps?.find(r => r.userId === currentUser?.id);
                  const userStatus = userRsvp?.status;
                  const hadirCount = notice.rsvps?.filter(r => r.status === 'Hadir').length || 0;
                  const uzurCount = notice.rsvps?.filter(r => r.status === 'Tidak Hadir' || (r.status as string) === 'Uzur').length || 0;
                  const confirmLabel = notice.actionConfirm || 'Hadir 🔥';
                  const declineLabel = notice.actionDecline || 'Cuti / Uzur 🤒';

                  return (
                    <div
                      key={notice.id}
                      className="bg-white rounded-3xl border border-slate-200 hover:border-slate-350 shadow-sm hover:shadow-md transition-all p-5 sm:p-6 space-y-3.5 relative overflow-hidden group"
                    >
                      {/* Top Header: Logo + PSSASMM PWA + Timestamp */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#0B0F19] overflow-hidden flex items-center justify-center p-0.5 border border-slate-800 shadow-2xs shrink-0">
                            <img src="/icons/icon-192.png" alt="PSSASMM" className="w-full h-full object-contain" />
                          </div>
                          <span className="text-xs font-black text-slate-800 tracking-wide flex items-center gap-1.5">
                            <span>PSSASMM PWA</span>
                            <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-red-50 text-asmm-red border border-red-200/70">
                              Latihan Gelanggang
                            </span>
                          </span>
                        </div>
                        <span className="text-[11px] font-semibold text-slate-400">
                          Sekarang
                        </span>
                      </div>

                      {/* Title & Body */}
                      <div className="space-y-1">
                        <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-snug">
                          {notice.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                          {notice.content}
                        </p>
                      </div>

                      {/* 2 Big Action Buttons: Hadir 🔥 vs Cuti / Uzur 🤒 */}
                      <div className="grid grid-cols-2 gap-2.5 pt-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRsvpSubmit(notice.id, 'Hadir');
                          }}
                          className={`py-2.5 px-3 sm:px-5 rounded-2xl font-black text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none shadow-xs ${userStatus === 'Hadir'
                            ? 'bg-emerald-600 text-white ring-2 ring-emerald-500 shadow-sm'
                            : 'bg-asmm-red hover:bg-asmm-crimson text-white active:scale-98'
                            }`}
                        >
                          {userStatus === 'Hadir' && <Check className="w-4 h-4 text-emerald-100" />}
                          <span>{userStatus === 'Hadir' ? 'Sah Hadir' : confirmLabel}</span>
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRsvpSubmit(notice.id, 'Tidak Hadir');
                          }}
                          className={`py-2.5 px-3 sm:px-5 rounded-2xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none ${userStatus === 'Tidak Hadir'
                            ? 'bg-rose-600 text-white ring-2 ring-rose-500 shadow-sm'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-800 active:scale-98'
                            }`}
                        >
                          {userStatus === 'Tidak Hadir' && <Check className="w-4 h-4 text-rose-100" />}
                          <span>{userStatus === 'Tidak Hadir' ? 'Sah Cuti / Uzur' : declineLabel}</span>
                        </button>
                      </div>

                      {/* Card Footer: Live Counts & Management Actions */}
                      <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
                            {hadirCount} Sah Hadir
                          </span>
                          <span className="font-bold text-slate-600 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200/60">
                            {uzurCount} Cuti / Uzur
                          </span>
                          {notice.targetGelanggang && (
                            <span className="font-semibold text-slate-400">
                              • {notice.targetGelanggang}
                            </span>
                          )}
                        </div>

                        {isManagement && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(notice)}
                              className="p-1 text-slate-400 hover:text-blue-600 rounded-md transition"
                              title="Kemaskini"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setNoticeToDelete(notice)}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded-md transition"
                              title="Padam"
                            >
                              <Trash2 className="w-3 h-3 text-rose-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                const rsvpButtonText = notice.type === 'acara'
                  ? 'Sahkan Kehadiran'
                  : notice.type === 'poll'
                    ? 'Undi Pilihan'
                    : 'Lihat Butiran';

                if (effectiveLayout === 'list') {
                  return (
                    <div
                      key={notice.id}
                      onClick={() => navigateToNotice(notice.id)}
                      className="bg-white p-5 rounded-3xl border border-slate-200 hover:border-slate-350 hover:shadow-2xs transition-all flex flex-col sm:flex-row justify-between sm:items-center gap-4 cursor-pointer group"
                    >
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="flex items-center gap-2.5">
                          <span className={`text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border ${notice.type === 'acara'
                            ? 'bg-red-50 text-asmm-red border-red-200'
                            : notice.type === 'pengumuman'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                            {notice.type === 'acara' ? 'Acara / Latihan (RSVP)' : notice.type === 'pengumuman' ? 'Pengumuman' : 'Undian (Poll)'}
                          </span>
                          {notice.hasFamilyAddon && (
                            <span className="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5 text-amber-600" />
                              <span>Add-On Keluarga</span>
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 font-semibold">{notice.createdAt}</span>
                        </div>

                        <div className="space-y-1">
                          <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-asmm-red transition-colors leading-snug">{notice.title}</h3>
                          <p className="text-[11px] text-slate-600 truncate max-w-lg sm:max-w-2xl">{notice.content}</p>
                        </div>

                        {notice.type === 'acara' && (
                          <div className="text-[10px] text-slate-500 font-bold flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-asmm-red" />
                            <span>{formatEventDates(notice.eventDate, notice.eventEndDate)} | {notice.eventTime} | {notice.eventLocation}</span>
                          </div>
                        )}

                        <div className="text-[9px] text-slate-400 font-semibold">
                          Oleh: <span className="text-slate-600 font-bold">{notice.authorName}</span> • Sasaran: <span className="text-asmm-red font-bold">{notice.targetGelanggang}</span>
                        </div>
                      </div>

                      <div className="shrink-0 pl-11 sm:pl-0 flex items-center gap-2">
                        {isManagement && (
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(notice)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 rounded-xl transition-all cursor-pointer bg-white"
                              title="Kemaskini Notis"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setNoticeToDelete(notice)}
                              className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 rounded-xl transition-all cursor-pointer bg-white"
                              title="Padam Notis"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          className="px-3.5 py-2 bg-slate-900 group-hover:bg-asmm-red text-white text-[10px] font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1 cursor-pointer border-none font-sans"
                        >
                          <span>{rsvpButtonText}</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={notice.id}
                    onClick={() => navigateToNotice(notice.id)}
                    className="bg-white p-5 rounded-3xl border border-slate-200 hover:border-slate-350 hover:shadow-2xs transition-all flex flex-col justify-between gap-4 cursor-pointer group"
                  >
                    <div className="space-y-3 flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border ${notice.type === 'acara'
                            ? 'bg-red-50 text-asmm-red border-red-200'
                            : notice.type === 'pengumuman'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                            {notice.type === 'acara' ? 'Acara / Latihan (RSVP)' : notice.type === 'pengumuman' ? 'Pengumuman' : 'Undian (Poll)'}
                          </span>
                          {notice.hasFamilyAddon && (
                            <span className="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5 text-amber-600" />
                              <span>Add-On Keluarga</span>
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 font-semibold">{notice.createdAt}</span>
                      </div>

                      <div className="space-y-1">
                        <h3 className="text-xs sm:text-sm font-extrabold text-slate-950 group-hover:text-asmm-red transition-colors leading-snug">{notice.title}</h3>
                        <p className="text-[11px] text-slate-600 line-clamp-2">{notice.content}</p>
                      </div>

                      {notice.type === 'acara' && (
                        <div className="text-[10px] text-slate-500 font-bold flex items-center gap-1.5 bg-slate-50 p-2 rounded-xl border border-slate-100">
                          <Calendar className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                          <span className="truncate">{formatEventDates(notice.eventDate, notice.eventEndDate)} | {notice.eventTime} | {notice.eventLocation}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-[9px] text-slate-400 font-semibold">
                      <span>Oleh: <strong className="text-slate-650">{notice.authorName}</strong></span>
                      <div className="flex items-center gap-1.5">
                        {isManagement && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(notice)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 rounded-lg transition-all cursor-pointer bg-white"
                              title="Kemaskini Notis"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setNoticeToDelete(notice)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 rounded-lg transition-all cursor-pointer bg-white"
                              title="Padam Notis"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          className="px-3 py-1.5 bg-slate-900 group-hover:bg-asmm-red text-white text-[9px] font-extrabold rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer border-none font-sans"
                        >
                          <span>{rsvpButtonText}</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* CREATE / EDIT NOTICE MODAL */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === 'create' ? 'Cipta Notis Baharu' : 'Kemaskini Notis'}
      >
        <form onSubmit={handleSaveNotice} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Notis</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Contoh: Latihan Berpusat & Bengkung Putih"
              className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Notis</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as NoticeType)}
              className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            >
              <option value="acara">Acara</option>
              <option value="Peringatan Latihan">Peringatan Latihan (RSVP PWA)</option>
              <option value="pengumuman">Pengumuman</option>
              <option value="poll">Poll</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Gelanggang Sasaran</label>
            <select
              value={targetGelanggang}
              onChange={(e) => setTargetGelanggang(e.target.value as Gelanggang)}
              className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            >
              <option value="Semua Gelanggang">Semua</option>
              <option value="Pusaka Warisan">Pusaka Warisan</option>
              <option value="Wadi AnNour">Wadi AnNour</option>
              <option value="Paya Besar">Paya Besar</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Kandungan Notis</label>
            <textarea
              rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Tulis maklumat terperinci..."
              className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
            />
          </div>

          {type === 'acara' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2.5 bg-red-50 border border-red-200/80 rounded-xl">
                <span className="text-[11px] font-bold text-red-950">Templat Pantas Ujian Bengkung:</span>
                <button
                  type="button"
                  onClick={() => {
                    setTitle('Pendaftaran Ujian Kenaikan Bengkung & Tempahan Kemudahan Keluarga');
                    setContent(`Assalamualaikum w.b.t. dan Salam Sejahtera.\n\nPendaftaran bagi Ujian Kenaikan Bengkung kini dibuka kepada semua pelatih yang layak.\n\nMAKLUMAT PAKEJ PENDAFTARAN CALON:\nSetiap bayaran yuran pendaftaran bengkung telah merangkumi kemudahan percuma untuk ahli keluarga seperti berikut:\n2 Tempat Duduk - Percuma\n2 Pek Makanan - Percuma\n\nTEMPAHAN TAMBAHAN (ADD-ON):\nBagi ibu bapa atau waris yang ingin membawa ahli keluarga melebihi kuota percuma yang disediakan, tempahan tempat duduk dan makanan tambahan hendaklah dibuat secara berasingan mengikut ketetapan kadar berikut:\n\nTempat Duduk Tambahan:\nHarga Asal: RM 37 setiap kerusi\nHarga Promosi Awalan: RM 17 setiap kerusi\n\nMakanan Tambahan:\nHarga Asal: RM 18 setiap pax\nHarga Promosi Awalan: RM 13 setiap pax\n\nPENTING:\nKadar diskaun dan harga promosi awalan ini hanya sah untuk pembelian dan pengesahan yang dibuat sebelum atau pada 30 November 2026 sahaja. Selepas tarikh tersebut, semua pembelian tambahan akan dikenakan pada harga asal.\n\nLangkah ini diambil bagi memastikan susun atur kerusi di dewan mencukupi serta urusan tempahan katering berjalan lancar dan teratur.\n\nSila buat pendaftaran dan pengesahan kehadiran ahli keluarga tambahan kepada pihak pengurusan gelanggang dengan kadar segera.\n\nSekian, terima kasih.`);
                    setEventDate('2026-12-11');
                    setEventEndDate('2026-12-12');
                    setIsMultiDay(true);
                    setEventTime('8.00 Pagi - 2.00 Petang');
                    setEventLocation('Pusat Riadah Wadi AnNour');
                    setRegistrationDeadline('2026-12-10');
                    setTargetGelanggang('Semua Gelanggang');
                    setCtaButtonText('Daftar Sekarang');
                    setCtaButtonLink('/ujian-bengkung');
                  }}
                  className="px-2.5 py-1 bg-asmm-red text-white text-[10px] font-extrabold rounded-lg hover:bg-asmm-crimson transition-all cursor-pointer shadow-2xs"
                >
                  Isi Templat Ujian Bengkung
                </button>
              </div>

              {/* Event Schedule & Details with 2-Day Support */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-asmm-red" />
                      Jadual & Lokasi Acara
                    </span>
                    <label className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-700 cursor-pointer select-none hover:bg-slate-50 transition-colors shadow-2xs">
                      <input
                        type="checkbox"
                        checked={isMultiDay}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsMultiDay(checked);
                          if (checked && !eventEndDate) {
                            if (eventDate === '2026-12-11') {
                              setEventEndDate('2026-12-12');
                            } else if (eventDate) {
                              try {
                                const d = new Date(eventDate);
                                d.setDate(d.getDate() + 1);
                                setEventEndDate(d.toISOString().split('T')[0]);
                              } catch {
                                setEventEndDate(eventDate);
                              }
                            } else {
                              setEventDate('2026-12-11');
                              setEventEndDate('2026-12-12');
                            }
                          }
                        }}
                        className="w-3.5 h-3.5 text-asmm-red rounded border-slate-300 focus:ring-0 cursor-pointer"
                      />
                      <span>Acara 2 Hari / Julat Tarikh</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setEventDate('2026-12-11');
                      setEventEndDate('2026-12-12');
                      setIsMultiDay(true);
                      if (!eventTime) setEventTime('8.00 Pagi - 2.00 Petang');
                      if (!eventLocation) setEventLocation('Pusat Riadah Wadi AnNour');
                      if (!registrationDeadline) setRegistrationDeadline('2026-12-10');
                    }}
                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer shadow-2xs"
                    title="Tetapkan automatik 11 & 12 Disember 2026 (2 Hari)"
                  >
                    <Sparkles className="w-3 h-3 text-amber-600" />
                    <span>⚡ Set 11 & 12 Dis 2026 (2 Hari)</span>
                  </button>
                </div>

                <div className={`grid grid-cols-1 sm:grid-cols-2 ${isMultiDay ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-2.5`}>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      {isMultiDay ? 'Tarikh Mula (Hari 1)' : 'Tarikh Event / Acara'}
                    </label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => {
                        setEventDate(e.target.value);
                        if (isMultiDay && !eventEndDate) {
                          setEventEndDate(e.target.value);
                        }
                      }}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900 font-medium focus:outline-hidden focus:border-asmm-red"
                    />
                  </div>

                  {isMultiDay && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Tarikh Akhir (Hari 2)
                      </label>
                      <input
                        type="date"
                        value={eventEndDate}
                        onChange={(e) => setEventEndDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900 font-medium focus:outline-hidden focus:border-asmm-red"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Masa</label>
                    <input
                      type="text"
                      value={eventTime}
                      onChange={(e) => setEventTime(e.target.value)}
                      placeholder="8.00 Pagi - 2.00 Petang"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900 focus:outline-hidden focus:border-asmm-red"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Lokasi</label>
                    <input
                      type="text"
                      value={eventLocation}
                      onChange={(e) => setEventLocation(e.target.value)}
                      placeholder="Pusat Riadah Wadi AnNour"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900 focus:outline-hidden focus:border-asmm-red"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Pendaftaran Tamat</label>
                    <input
                      type="date"
                      value={registrationDeadline}
                      onChange={(e) => setRegistrationDeadline(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900 focus:outline-hidden focus:border-asmm-red"
                    />
                  </div>
                </div>

                {/* Formatted Date Preview */}
                {eventDate && (
                  <div className="pt-1.5 flex items-center justify-between text-[11px] border-t border-slate-200/60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-500 font-semibold">Paparan Notis:</span>
                      <span className="font-extrabold text-slate-900 bg-white px-2.5 py-0.5 rounded-md border border-slate-200 shadow-2xs">
                        {formatEventDatesFull(eventDate, isMultiDay ? eventEndDate : undefined)}
                      </span>
                      {isMultiDay && eventEndDate && eventEndDate !== eventDate && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-black rounded-md text-[10px] border border-blue-200">
                          2 Hari Acara
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Pakej Kemudahan Keluarga & Tempahan Tambahan (Add-On) Editable Fields */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasFamilyAddon}
                      onChange={(e) => setHasFamilyAddon(e.target.checked)}
                      className="w-4 h-4 rounded text-red-600 focus:ring-red-500 border-slate-300"
                    />
                    <span className="text-xs font-black text-slate-900">
                      Aktifkan Pakej Kemudahan Keluarga & Tempahan Tambahan (Add-On)
                    </span>
                  </label>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${hasFamilyAddon ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600'}`}>
                    {hasFamilyAddon ? 'Diaktifkan' : 'Dimatikan'}
                  </span>
                </div>

                {hasFamilyAddon && (
                  <div className="space-y-3 pt-2 border-t border-slate-200/80">
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Tetapkan kuota kemudahan percuma bagi setiap calon, kadar tempat duduk dan makanan tambahan, serta tarikh tamat promosi:
                    </p>

                    {/* Kuota Percuma Calon */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-2.5 bg-white rounded-xl border border-slate-200">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Tempat Duduk Percuma / Calon
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="0"
                            value={freeSeatsPerPax}
                            onChange={(e) => setFreeSeatsPerPax(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-900"
                          />
                          <span className="text-[11px] text-slate-500 font-semibold shrink-0">Kerusi</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Pek Makanan Percuma / Calon
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="0"
                            value={freeMealsPerPax}
                            onChange={(e) => setFreeMealsPerPax(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-900"
                          />
                          <span className="text-[11px] text-slate-500 font-semibold shrink-0">Pek</span>
                        </div>
                      </div>
                    </div>

                    {/* Kadar Kerusi Tambahan */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-2.5 bg-blue-50/50 rounded-xl border border-blue-200">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Kerusi Tambahan (Harga Asal)
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-slate-500">RM</span>
                          <input
                            type="number"
                            min="0"
                            value={seatOriginalPrice}
                            onChange={(e) => setSeatOriginalPrice(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-900 bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-blue-900 mb-1">
                          Kerusi Tambahan (Promosi Awalan)
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-blue-600">RM</span>
                          <input
                            type="number"
                            min="0"
                            value={seatPromoPrice}
                            onChange={(e) => setSeatPromoPrice(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-blue-300 text-xs font-bold text-blue-900 bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Kadar Makanan Tambahan */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-2.5 bg-amber-50/50 rounded-xl border border-amber-200">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Makanan Tambahan (Harga Asal)
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-slate-500">RM</span>
                          <input
                            type="number"
                            min="0"
                            value={mealOriginalPrice}
                            onChange={(e) => setMealOriginalPrice(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-900 bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-amber-900 mb-1">
                          Makanan Tambahan (Promosi Awalan)
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-amber-600">RM</span>
                          <input
                            type="number"
                            min="0"
                            value={mealPromoPrice}
                            onChange={(e) => setMealPromoPrice(Number(e.target.value))}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-amber-300 text-xs font-bold text-amber-900 bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tarikh Tamat Promosi Awalan */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Tarikh Akhir Promosi Awalan
                      </label>
                      <input
                        type="date"
                        value={promoDeadline}
                        onChange={(e) => setPromoDeadline(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-900 bg-white"
                      />
                      <span className="text-[10px] text-slate-500 mt-1 block">
                        Selepas tarikh ini, semua tempahan tambahan akan dikenakan harga asal secara automatik.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {type === 'pengumuman' && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <label className="block text-[11px] font-bold text-slate-700 mb-1">Teks Butang Tindakan (CTA)</label>
              <input
                type="text"
                value={ctaButtonText}
                onChange={(e) => setCtaButtonText(e.target.value)}
                placeholder="Contoh: Bayar Yuran / Daftar Bengkung"
                className="w-full px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900"
              />
            </div>
          )}

          {type === 'poll' && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <label className="block text-[11px] font-bold text-slate-700 mb-1">Pilihan Poll (Pisahkan dengan koma)</label>
              <input
                type="text"
                value={pollOptionsInput}
                onChange={(e) => setPollOptionsInput(e.target.value)}
                placeholder="Pilihan 1, Pilihan 2, Pilihan 3"
                className="w-full px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-900"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson cursor-pointer shadow-xs"
            >
              {modalMode === 'edit' ? 'Simpan Perubahan' : 'Terbitkan Notis'}
            </button>
          </div>
        </form>
      </Modal>

      {/* BREAKDOWN RESULTS MODAL */}
      {breakdownNotice && (
        <Modal
          isOpen={!!breakdownNotice}
          onClose={() => setBreakdownNotice(null)}
          title={`Pecahan Keputusan (Breakdown Result) - ${breakdownNotice.title}`}
        >
          <div className="space-y-6">
            {breakdownNotice.type === 'acara' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-center">
                    <div className="text-xs font-bold text-emerald-800">Hadir</div>
                    <div className="text-2xl font-extrabold text-emerald-700">
                      {breakdownNotice.rsvps?.filter(r => r.status === 'Hadir').length || 0}
                    </div>
                  </div>
                  <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-center">
                    <div className="text-xs font-bold text-rose-800">Tidak Hadir</div>
                    <div className="text-2xl font-extrabold text-rose-700">
                      {breakdownNotice.rsvps?.filter(r => r.status === 'Tidak Hadir').length || 0}
                    </div>
                  </div>
                  <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-center">
                    <div className="text-xs font-bold text-amber-800">Ragu-ragu</div>
                    <div className="text-2xl font-extrabold text-amber-700">
                      {breakdownNotice.rsvps?.filter(r => r.status === 'Ragu-ragu').length || 0}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-900 uppercase">Senarai Respons Ahli:</h4>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {breakdownNotice.rsvps?.map((r, i) => (
                      <div key={i} className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                        <div>
                          <div className="font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
                            <span>{r.userName} ({r.role})</span>
                            {r.userId && (
                              <span className="font-mono text-[10px] text-slate-500 font-bold bg-slate-200/70 px-1 py-0.2 rounded">
                                {sanitizeMemberId(r.userId)}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500">{r.gelanggang} {r.reason && `• Catatan: "${r.reason}"`}</div>
                        </div>
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${r.status === 'Hadir' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                          r.status === 'Tidak Hadir' ? 'bg-rose-100 text-rose-800 border-rose-200' : 'bg-amber-100 text-amber-800 border-amber-200'
                          }`}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {breakdownNotice.type === 'poll' && (
              <div className="space-y-4">
                <div className="space-y-3">
                  {breakdownNotice.pollOptions?.map((opt) => {
                    const totalVotes = breakdownNotice.pollOptions?.reduce((sum, item) => sum + item.count, 0) || 1;
                    const pct = Math.round((opt.count / totalVotes) * 100);

                    return (
                      <div key={opt.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                        <div className="flex justify-between text-xs font-bold text-slate-900">
                          <span>{opt.text}</span>
                          <span className="text-asmm-red">{opt.count} Undi ({pct}%)</span>
                        </div>
                        <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                          <div
                            className="bg-asmm-red h-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
      {/* CUSTOM DELETE CONFIRMATION MODAL - NO BROWSER NATIVE UI */}
      {noticeToDelete && (
        <Modal
          isOpen={!!noticeToDelete}
          onClose={() => !isDeletingNotice && setNoticeToDelete(null)}
          title="Sahkan Pemadaman Notis"
          maxWidth="max-w-md"
        >
          <div className="space-y-4 pt-1">
            <div className="flex items-start gap-3.5 p-3.5 bg-rose-50/70 border border-rose-200/80 rounded-2xl">
              <div className="p-2 bg-rose-100 text-rose-600 rounded-xl shrink-0 mt-0.5">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="space-y-1 text-left">
                <div className="text-xs font-bold text-rose-950">
                  Padam "{noticeToDelete.title}"?
                </div>
                <p className="text-[11px] text-rose-800 leading-relaxed font-normal">
                  Tindakan ini kekal dan akan memadam notis ini berserta semua data RSVP / undian berkaitan daripada pangkalan data aaPanel.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={isDeletingNotice}
                onClick={() => setNoticeToDelete(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer border-none disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeletingNotice}
                onClick={handleConfirmDeleteNotice}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50"
              >
                {isDeletingNotice ? (
                  <span>Memadam...</span>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Ya, Padam Notis</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* MODAL PENGURUSAN BUTIRAN BANK & QR (ADMIN) */}
      {isQrModalOpen && (
        <Modal
          isOpen={isQrModalOpen}
          onClose={() => setIsQrModalOpen(false)}
          title="Tetapan Butiran Bank & DuitNow QR Ujian Bengkung"
          maxWidth="max-w-lg"
        >
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-2.5">
              <QrCode className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong className="block text-amber-950 font-bold">Pengurusan Pilihan Bayaran DuitNow QR</strong>
                <span className="text-amber-800 text-[11px] leading-relaxed">
                  Butiran ini akan dipaparkan secara automatik kepada ibu bapa dan pesilat di Halaman Pendaftaran Ujian Bengkung apabila kaedah QR diaktifkan.
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Nama Bank Rasmi / Penerima
                </label>
                <input
                  type="text"
                  value={editQrBankName}
                  onChange={(e) => setEditQrBankName(e.target.value)}
                  placeholder="Contoh: Bank Islam Malaysia Berhad"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs focus:bg-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Nama Pemegang Akaun
                </label>
                <input
                  type="text"
                  value={editQrAccountName}
                  onChange={(e) => setEditQrAccountName(e.target.value)}
                  placeholder="Contoh: PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs focus:bg-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Nombor Akaun Bank
                </label>
                <input
                  type="text"
                  value={editQrAccountNo}
                  onChange={(e) => setEditQrAccountNo(e.target.value)}
                  placeholder="Contoh: 02011010087654"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 font-mono text-xs focus:bg-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Arahan / Rujukan Pindahan
                </label>
                <textarea
                  rows={2}
                  value={editQrNotes}
                  onChange={(e) => setEditQrNotes(e.target.value)}
                  placeholder="Contoh: Sila masukkan rujukan 'UB2026 - [Nama Calon]'"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs focus:bg-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsQrModalOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleSaveQrDetails}
                className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition shadow-xs cursor-pointer"
              >
                Simpan Butiran
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
